import React, { useState, useEffect } from "react";
import { 
  Table, Tag, Typography, Input, Select, Button, Row, Col, Card, 
  Spin, Empty, Space, message 
} from "antd";
import { 
  AppleOutlined, 
  AndroidOutlined, 
  WindowsOutlined, 
  ChromeOutlined, 
  MobileOutlined,
  DesktopOutlined,
  QuestionOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined 
} from "@ant-design/icons";
import axios from "axios";

const { Search } = Input;
const { Option } = Select;

const EventsLogs = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    eventType: null,
    role: null,
    description: null,
  });

  const storedDeviceId = localStorage.getItem("deviceId");
  const storedUserIp = localStorage.getItem("userIp");

  // LOAD EVENTS
  const loadEvents = async () => {
    try {
      setLoading(true);

      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) {
        message.error("Authentication required. Please login first.");
        setLoading(false);
        return;
      }

      const response = await axios.post(
        "https://beyondschools.co.tz:8443/api/Admin/get-event-logs",
        { username: "" },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            UserDevice: storedDeviceId,
            UserIPAddress: storedUserIp,
          },
        }
      );

      const data = response.data;

      if (data.code !== 200 || !Array.isArray(data.data)) {
        message.error(data.message || "Unexpected API response");
        return;
      }

      // Format results
      const formatted = data.data.map(item => {
        const username = item.username;
        const isEmail = username?.includes("@");

        return {
          eventLogId: item.eventLogId,
          username,
          email: isEmail ? username : null,
          phonenumber: !isEmail ? username : null,
          role: item.role,
          roleId: item.roleId,
          event_type: item.eventType,
          description: item.eventDescription,
          ip_address: item.deviceIp,
          device: item.device,
          created_at: item.eventCreatedDateAt,
          name: item.name
        };
      });

      setEvents(formatted);
      setFilteredEvents(formatted);

    } catch (error) {
      console.error("Error loading events:", error);
      if (error.response) {
        if (error.response.status === 401) message.error("Session expired. Please login again.");
        else if (error.response.status === 403) message.error("You don't have permission to view event logs.");
        else message.error(error.response.data?.message || "Failed to load events");
      } else {
        message.error("Network or server error. Try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  // SEARCH
  const handleSearch = (value) => {
    setSearchText(value);
    const keyword = value.toLowerCase().trim();

    if (!keyword) {
      setFilteredEvents(events);
      return;
    }

    const filtered = events.filter(e =>
      (e.username || "").toLowerCase().includes(keyword) ||
      (e.email || "").toLowerCase().includes(keyword) ||
      (e.phonenumber || "").toLowerCase().includes(keyword) ||
      (e.event_type || "").toLowerCase().includes(keyword) ||
      (e.role || "").toLowerCase().includes(keyword) ||
      (e.name || "").toLowerCase().includes(keyword)
    );

    setFilteredEvents(filtered);
  };

  // FILTERS
  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);

    let filtered = [...events];

    // text search
    if (searchText) {
      const keyword = searchText.toLowerCase().trim();
      filtered = filtered.filter(e =>
        (e.username || "").toLowerCase().includes(keyword) ||
        (e.email || "").toLowerCase().includes(keyword) ||
        (e.phonenumber || "").toLowerCase().includes(keyword) ||
        (e.event_type || "").toLowerCase().includes(keyword) ||
        (e.role || "").toLowerCase().includes(keyword) ||
        (e.name || "").toLowerCase().includes(keyword)
      );
    }

    // event type filter
    if (newFilters.eventType) {
      filtered = filtered.filter(e => e.event_type === newFilters.eventType);
    }

    // role filter
    if (newFilters.role) {
      filtered = filtered.filter(e => e.role === newFilters.role);
    }

    // description filter
    if (newFilters.description) {
      filtered = filtered.filter(e =>
        (e.description || "").toLowerCase().includes(newFilters.description.toLowerCase())
      );
    }

    setFilteredEvents(filtered);
  };

  // RESET FILTERS
  const handleResetFilters = () => {
    setSearchText("");
    setFilters({
      eventType: null,
      role: null,
      description: null,
    });
    setFilteredEvents(events);
  };

  // UNIQUE FILTER OPTIONS
  const eventTypes = [...new Set(events.map(e => e.event_type).filter(Boolean))];
  const roles = [...new Set(events.map(e => e.role).filter(Boolean))];

  // DEVICE ICON HANDLER
  const getDeviceIcon = (device) => {
    if (!device) return <QuestionOutlined />;
    
    const os = device?.os?.family?.toLowerCase() || "";
    const ua = device?.ua?.family?.toLowerCase() || "";
    const family = device?.device?.family?.toLowerCase() || "";

    if (os.includes("ios") || os.includes("mac")) return <AppleOutlined />;
    if (os.includes("android")) return <AndroidOutlined />;
    if (os.includes("windows")) return <WindowsOutlined />;
    if (ua.includes("chrome")) return <ChromeOutlined />;
    if (family.includes("mobile")) return <MobileOutlined />;

    return <DesktopOutlined />;
  };

  const formatDeviceInfo = (device) => {
    if (!device) return "Unknown Device";
    const parts = [];
    if (device.os?.family) parts.push(device.os.family);
    if (device.ua?.family) parts.push(device.ua.family);
    return parts.length > 0 ? parts.join(" · ") : "Unknown Device";
  };

  // TABLE COLUMNS
  const columns = [
    {
      title: "#",
      width: 50,
      render: (text, record, index) => index + 1,
    },
    {
      title: "Event",
      dataIndex: "event_type",
      render: (text) => (
        <Tag color={
          text?.includes("Success") ? "success" :
          text?.includes("Failed") ? "error" :
          text?.includes("Login") ? "processing" :
          "default"
        }>
          {text}
        </Tag>
      )
    },
    {
      title: "Description",
      dataIndex: "description",
      width: 120,
      render: (text) => (
        <Typography.Paragraph style={{ margin: 0, fontSize: 12 }}>
          {text}
        </Typography.Paragraph>
      )
    },
    {
      title: "Username",
      dataIndex: "username",
      render: (text) => <Typography.Text strong>{text}</Typography.Text>,
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text) => text || "N/A",
    },
    {
      title: "Role",
      dataIndex: "role",
      render: (role) => (
        <Tag color={
          role === "Admin" ? "red" :
          role === "Student" ? "blue" :
          role === "Teacher" ? "green" :
          "orange"
        }>
          {role}
        </Tag>
      )
    },
    {
      title: "IP",
      dataIndex: "ip_address",
      render: (ip) => <Tag color="purple">{ip}</Tag>,
    },
    {
      title: "Device",
      dataIndex: "device",
      render: (device) => (
        <Space>
          {getDeviceIcon(device)}
          <Typography.Text type="secondary">
            {formatDeviceInfo(device)}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: "Date",
      dataIndex: "created_at",
      width: 130,
      render: (date) => date ? new Date(date).toLocaleString() : "N/A",
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      defaultSortOrder: "descend",
    }
  ];

  return (
    <Card
      title={
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>Event Logs</span>
          <Button icon={<ReloadOutlined />} onClick={loadEvents} loading={loading}>
            Reload
          </Button>
        </div>
      }
    >
      {/* SEARCH & FILTERS */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} md={8}>
          <Search
            placeholder="Search username, event, role..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            onSearch={handleSearch}
          />
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Select
            placeholder="Filter by Event Type"
            style={{ width: "100%" }}
            value={filters.eventType}
            onChange={(value) => handleFilterChange("eventType", value)}
            allowClear
            suffixIcon={<FilterOutlined />}
          >
            {eventTypes.map((t) => (
              <Option key={t} value={t}>{t}</Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Select
            placeholder="Filter by Role"
            style={{ width: "100%" }}
            value={filters.role}
            onChange={(value) => handleFilterChange("role", value)}
            allowClear
            suffixIcon={<FilterOutlined />}
          >
            {roles.map((r) => (
              <Option key={r} value={r}>{r}</Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <Button style={{ width: "100%" }} onClick={handleResetFilters}>
            Reset Filters
          </Button>
        </Col>
      </Row>

      {/* COUNTS */}
      <Typography.Text strong>
        Total Events: {events.length}
      </Typography.Text>

      {filteredEvents.length !== events.length && (
        <Typography.Text type="secondary" style={{ marginLeft: 15 }}>
          Filtered: {filteredEvents.length}
        </Typography.Text>
      )}

      {/* TABLE */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
        </div>
      ) : filteredEvents.length === 0 ? (
        <Empty description="No events match your filters" />
      ) : (
        <Table
          dataSource={filteredEvents}
          columns={columns}
          rowKey="eventLogId"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} events`,
          }}
          scroll={{ x: 1100 }}
        />
      )}
    </Card>
  );
};

export default EventsLogs;
